import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart, Maximize2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import ImageLightbox from "./ImageLightbox";
import { pcImageUrl, pcImageFallback } from "../lib/pcImage";
import { CONDITION_LABELS } from "../lib/conditions";
import { useChaseState, toggleChase } from "../lib/chase";

const resolveUrl = (url) => (url?.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

function StatRow({ label, value, testId }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 border-b border-white/5 last:border-0">
      <span className="text-[11px] uppercase tracking-widest text-zinc-600 shrink-0">{label}</span>
      <span data-testid={testId} className="text-sm text-zinc-200 text-right capitalize">{value}</span>
    </div>
  );
}

export default function PublicCardDialog({ card, open, onOpenChange, fmt, ownerName, actions }) {
  const { user } = useAuth();
  const [lightbox, setLightbox] = useState(false);
  const chased = useChaseState(card, !!user);

  useEffect(() => {
    if (!open) setLightbox(false);
  }, [open]);

  if (!card) return null;

  const scan = card.captured_image_url ? resolveUrl(card.captured_image_url) : null;
  const match = card.reference_image_url ? pcImageUrl(card.reference_image_url, "1600") : null;
  const wantScan = (card.display_image || (card.reference_image_url ? "match" : "scan")) === "scan";
  const img = wantScan ? (scan || match) : (match || scan);
  const imgLabel = img && img === scan ? "Their scan" : "Web match";

  let conditionText;
  if (card.copy_conditions?.length > 1) {
    const counts = card.copy_conditions.reduce((acc, c) => ({ ...acc, [c]: (acc[c] || 0) + 1 }), {});
    conditionText = Object.entries(counts).map(([c, n]) => `${n}× ${CONDITION_LABELS[c] || c}`).join(", ");
  } else {
    const cond = card.condition || "NM";
    conditionText = CONDITION_LABELS[cond] || cond;
  }

  const handleChase = async () => {
    const result = await toggleChase(card);
    if (result === "added") {
      toast.success(`"${card.name}" added to your Chase Deck`);
    } else if (result === "removed") {
      toast.info(`"${card.name}" removed from your Chase Deck`);
    } else if (result === "duplicate") {
      toast.info("Already in your Chase Deck");
    } else if (result === "auth") {
      toast.error("Log in to build your Chase Deck");
    } else {
      toast.error("Couldn't update your Chase Deck");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="public-card-dialog"
          className="bg-[#111113] border-white/10 text-white sm:max-w-2xl"
          onEscapeKeyDown={(e) => {
            if (lightbox) {
              e.preventDefault();
              setLightbox(false);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle data-testid="public-card-dialog-title" className="font-display text-xl pr-6">
              {card.name}
              {card.card_number && <span className="text-zinc-500 font-normal text-base ml-2">#{card.card_number}</span>}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              {[card.set_name, ownerName && `from ${ownerName}'s binder`].filter(Boolean).join(" · ")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-5">
            <div className="relative mx-auto sm:mx-0 w-[200px] sm:w-[220px]">
              {img ? (
                <button type="button" onClick={() => setLightbox(true)} className="block w-full group relative" title="View full size">
                  <img src={img} onError={pcImageFallback} alt={card.name} className="w-full aspect-[3/4] object-cover rounded-xl border border-white/10" />
                  <span className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-zinc-900 border border-white/25 text-zinc-300 flex items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity">
                    <Maximize2 className="w-3.5 h-3.5" />
                  </span>
                  <span className="absolute top-2 left-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-zinc-900 border border-white/20 text-zinc-300">{imgLabel}</span>
                </button>
              ) : (
                <div className="w-full aspect-[3/4] rounded-xl border border-white/10 bg-black/40 flex items-center justify-center text-xs text-zinc-600 text-center px-3">{card.name}</div>
              )}
            </div>
            <div className="min-w-0">
              {card.current_price != null && (
                <div className="mb-3">
                  <p className="text-[11px] uppercase tracking-widest text-zinc-600">Market value</p>
                  <p data-testid="public-card-dialog-price" className="text-3xl font-display font-bold text-[#F59E0B] font-mono-plex">{fmt(card.current_price)}</p>
                </div>
              )}
              <StatRow label="Set" value={card.set_name} testId="public-card-stat-set" />
              <StatRow label="Card number" value={card.card_number} testId="public-card-stat-number" />
              <StatRow label="Rarity" value={card.rarity} testId="public-card-stat-rarity" />
              <StatRow label="Type" value={card.game_type} testId="public-card-stat-type" />
              <StatRow label="Condition" value={conditionText} testId="public-card-stat-condition" />
              <StatRow label="Copies" value={(card.quantity || 1) > 1 ? `×${card.quantity}` : null} testId="public-card-stat-quantity" />
              <StatRow label="Language" value={card.language} testId="public-card-stat-language" />
              {card.grade && <StatRow label="Grade" value={`${card.grading_company || ""} ${card.grade}`.trim()} testId="public-card-stat-grade" />}
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <Button
                  data-testid="public-card-chase-button"
                  variant="ghost"
                  onClick={handleChase}
                  className={`rounded-full border transition-colors ${chased ? "border-pink-400 bg-pink-500/15 text-pink-300" : "border-pink-400/50 bg-pink-500/10 text-pink-300 hover:bg-pink-500/20"}`}
                >
                  <Heart className={`w-4 h-4 mr-1.5 ${chased ? "fill-pink-400 text-pink-400" : ""}`} />
                  {chased ? "Remove from Chase Deck" : "Add to Chase Deck"}
                </Button>
                {actions}
              </div>
              {!user && (
                <p className="text-[10px] text-zinc-600 mt-2">Log in to save cards you're chasing — collectors get pinged when one appears.</p>
              )}
            </div>
          </div>
          {lightbox && img && (
            <ImageLightbox
              src={img}
              alt={card.name}
              label={`${card.name}${card.set_name ? ` · ${card.set_name}` : ""} · ${imgLabel}`}
              onClose={() => setLightbox(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
